import { AxiosInstance } from '../../../utilities/requestClient';
import {
  getReportsListURL,
  getRiskOverviewMonitorURL,
} from '../../../routes/endpoints/general.routes';
import { RSOverviewPageModel } from '../../api.types';
import type { ProspectusRestrictionsPageModel } from './prospectusRestrictions.types';
import type { StatusChangePageModel } from './statusChange.types';
import type {
  LawPageModel,
  LawName,
  LawHistoricalDataModel,
} from './law.types';
import type { ValueAtRiskPageModel } from './valueAtRisk.types';
import type { StressScenarioPageModel } from '../../../components/pages/ucits/stressTests/stressScenario.types';
import type { HistoricalPageModel } from './historicalData.types';
import type { SrriPageModel } from './srri.types';
import type { MrmPageModel } from './mrm.types';
import type { ShareClassHedgeSummaryPageModel } from './shareClassHedgeSummary.types';
import type { CollateralStressTestPageModel } from './collateralStressTest.types';
import type { FundPerformanceAnalysisPageModel } from './fundPerformanceAnalysis.types';
import type { RiskComparisonPageModel } from './riskComparison.types';
import type { RelativeRiskPageModel } from './relativeRisk.types';
import type { SrriShareClassPageModel } from './srriShareClassPage.types';
import type { RiskOverviewMonitorFundRow } from './riskOverview.types';
import type { SrriOverviewPageModel } from './srriOverview.types';
import type { MarketLookbackPageModel } from './marketLookback.types';
import type { Article48CoverageRulesPageModel } from './article48CoverageRules.types';
import type { GlobalExposurePageModel } from './globalExposure.types';
import type {
  CssfRiskSpreadingPageModel,
  CssfInscopePositionsResponse,
} from './cssfRiskSpreading.types';
import type { SupplementaryRulesPageModel } from './supplementaryRules.types';
import type { VarLimitOverviewPageModel } from './varLimitOverview.types';
import type {
  TimeSeriesKey,
  TimeSeriesParamsByKey,
  TimeSeriesResponseByKey,
} from './ruleTimeSeries.types';

export type FetchReportsListParams = {
  fundTypes?: string;
  reportType?: string;
  raptorPlatformId?: string;
  status?: string;
};

export type ReportsListByFund = Record<string, string[]>;

export const fetchReportsList = async ({
  fundTypes,
  reportType,
  raptorPlatformId,
  status,
}: FetchReportsListParams): Promise<ReportsListByFund> => {
  const { data } = await AxiosInstance.get<ReportsListByFund>(
    `/${getReportsListURL(fundTypes, reportType, raptorPlatformId, status)}`,
  );
  return data;
};

export type FetchOverviewMonitorParams = {
  positionDate?: string;
  fundTypes?: string;
  raptorPlatformId?: string;
  deceased?: boolean;
};

export const fetchRiskOverviewMonitor = async ({
  positionDate,
  fundTypes,
  raptorPlatformId,
  deceased,
}: FetchOverviewMonitorParams): Promise<
  RSOverviewPageModel<RiskOverviewMonitorFundRow[]>
> => {
  const { data } = await AxiosInstance.get<
    RSOverviewPageModel<RiskOverviewMonitorFundRow[]>
  >(
    `/${getRiskOverviewMonitorURL(
      positionDate || '',
      fundTypes,
      raptorPlatformId,
      deceased,
    )}`,
  );
  return data;
};

export interface FetchStatusChangeParams {
  fundTypes?: string;
  daysBack?: number;
}

/** GET /risk_decline_and_fall — status-change OverviewPage. */
export const getStatusChangePageURL = ({
  fundTypes,
  daysBack,
}: FetchStatusChangeParams): string => {
  const queryParams: string[] = [];

  if (fundTypes?.length) {
    queryParams.push(`schemas=${fundTypes}`);
  }
  if (daysBack) {
    queryParams.push(`days_back=${daysBack}`);
  }

  const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
  return `risk_decline_and_fall${queryString}`;
};

export const fetchStatusChangePage = async (
  params: FetchStatusChangeParams,
): Promise<StatusChangePageModel> => {
  const { data } = await AxiosInstance.get<StatusChangePageModel>(
    getStatusChangePageURL(params),
  );

  return data;
};

export interface ComparisonUcitsPosition {
  instrument_id: number;
  selected_position_date: string;
  client_price: number;
  bond_future_conversion_factor: number;
  used_fx_rate: number;
  used_position_size: number;
  used_price: number;
  instrument_type_id: number;
  country_code: string;
  used_bc_exposure: number;
  used_risk_factor: number;
  gm_id: number;
  risk_factor: number;
  g_key: string;
  strike: number | null;
  mr_position: number;
  gm_name_long: string;
  fx_rate: number;
  used_delta: number;
  expiry_date: string;
  isin: string;
  accrued_interest_local: number;
  exposure: number;
  accrued_interest_base: number;
  maturity_date: string;
  market_price: number;
  g_name: string;
  base_currency: string;
  [key: string]: any;
}

interface GetComparisonUcitsPositionsParams {
  fundId: string;
  issuer: string;
  activePositionDate: string;
}

export const fetchFundRiskComplianceData = async (
  fundId: string,
  positionDate?: string,
): Promise<ProspectusRestrictionsPageModel> => {
  const { data } = await AxiosInstance.get<ProspectusRestrictionsPageModel>(
    `prospectus_restrictions/overview/${fundId}/${positionDate}`,
  );

  return data;
};

const timeSeriesURL = (
  path: string,
  query: Record<string, string>,
  { startDate, endDate }: { startDate: string; endDate: string },
): string => {
  const params = new URLSearchParams({
    ...query,
    start_date: startDate,
    end_date: endDate,
  });
  return `${path}?${params.toString()}`;
};

const timeSeriesURLBuilders: {
  [K in TimeSeriesKey]: (params: TimeSeriesParamsByKey[K]) => string;
} = {
  /** GET /get_law_timeseries/<law_name>/<fund_name>?rule_key= */
  law: (params) =>
    timeSeriesURL(
      `get_law_timeseries/${encodeURIComponent(params.lawName)}/${encodeURIComponent(params.fundName)}`,
      { rule_key: params.ruleKey },
      params,
    ),
  /** GET /prospectus_restrictions/timeseries/<fund_name>?rule_index=&rule_name= */
  prospectus: (params) =>
    timeSeriesURL(
      `prospectus_restrictions/timeseries/${encodeURIComponent(params.fundName)}`,
      { rule_index: String(params.ruleIndex), rule_name: params.ruleName },
      params,
    ),
  /** GET /stored-cssf-risk-spreading-timeseries/<fund_name>?rule_name= */
  cssf: (params) =>
    timeSeriesURL(
      `stored-cssf-risk-spreading-timeseries/${encodeURIComponent(params.fundName)}`,
      { rule_name: params.ruleName },
      params,
    ),
  /** GET /supplementary-rules-timeseries/<fund_name>?rule_key= */
  supplementary: (params) =>
    timeSeriesURL(
      `supplementary-rules-timeseries/${encodeURIComponent(params.fundName)}`,
      { rule_key: params.ruleKey },
      params,
    ),
};

export const getTimeSeriesURL = <K extends TimeSeriesKey>(
  key: K,
  params: TimeSeriesParamsByKey[K],
): string => timeSeriesURLBuilders[key](params);

export const fetchTimeSeries = async <K extends TimeSeriesKey>(
  key: K,
  params: TimeSeriesParamsByKey[K],
): Promise<TimeSeriesResponseByKey[K]> => {
  const { data } = await AxiosInstance.get<TimeSeriesResponseByKey[K]>(
    getTimeSeriesURL(key, params),
  );
  return data;
};

export const fetchRiskReportDataKeys = async (
  fundId: string,
): Promise<string[]> => {
  const { data } = await AxiosInstance.get<string[]>(
    `get_risk_report_data_keys/${fundId}`,
  );
  return data;
};

export const getComparisonLawPositionsURL = (
  fundType: string,
  fundId: string,
  issuer: string,
  activePositionDate: string,
) =>
  `/get_t_minus_one_${fundType}_filtered_positions/${fundId}/${encodeURIComponent(issuer)}/${activePositionDate}`;

export const getComparisonUcitsPositions = async ({
  fundId,
  issuer,
  activePositionDate,
}: GetComparisonUcitsPositionsParams): Promise<ComparisonUcitsPosition[]> => {
  const { data } = await AxiosInstance.get<ComparisonUcitsPosition[]>(
    getComparisonLawPositionsURL('ucits', fundId, issuer, activePositionDate),
  );
  return data;
};

/**
 * Fetch consolidated Law page data.
 * GET /get_law/:lawName/:fundName
 */
export const fetchLawPage = async (
  lawName: LawName,
  fundName: string,
  positionDate: string,
): Promise<LawPageModel> => {
  const { data } = await AxiosInstance.get<LawPageModel>(
    `get_law/${lawName}/${fundName}/${positionDate}`,
  );

  return data;
};

/**
 * Fetch consolidated Value at Risk page data.
 * GET /value_at_risk_page/:fundName/:positionDate
 */
export const fetchValueAtRiskPage = async (
  fundName: string,
  positionDate: string,
): Promise<ValueAtRiskPageModel> => {
  const { data } = await AxiosInstance.get<ValueAtRiskPageModel>(
    `value_at_risk_page/${fundName}/${positionDate}`,
  );

  return data;
};

export const getStressScenarioTestsURL = (
  fundName: string,
  selectedPositionDate: string,
) => `get-stress-scenario-tests/${fundName}/${selectedPositionDate}`;

export const fetchStressScenarioTests = async (
  fundName: string,
  selectedPositionDate: string,
): Promise<StressScenarioPageModel> => {
  const { data } = await AxiosInstance.get<StressScenarioPageModel>(
    getStressScenarioTestsURL(fundName, selectedPositionDate),
  );
  return data;
};

export interface FetchHistoricalDataParams {
  fundName: string;
  date1: string;
  date2: string;
  key1: string;
  key2: string;
}

export const getHistoricalDataURL = ({
  fundName,
  date1,
  date2,
  key1,
  key2,
}: FetchHistoricalDataParams): string =>
  `get-historical-data/${fundName}/${date1}/${date2}/${key1}/${key2}`;

export const fetchHistoricalData = async (
  params: FetchHistoricalDataParams,
): Promise<HistoricalPageModel> => {
  const { data } = await AxiosInstance.get<HistoricalPageModel>(
    getHistoricalDataURL(params),
  );

  return data;
};

export interface FetchLawHistoricalDataParams {
  fundName: string;
  startDate: string;
  endDate: string;
}

export const getLawHistoricalDataURL = ({
  fundName,
  startDate,
  endDate,
}: FetchLawHistoricalDataParams): string =>
  `aggregate_monthly_review_d/${fundName}/${startDate}/${endDate}`;

export const fetchLawHistoricalData = async (
  params: FetchLawHistoricalDataParams,
): Promise<LawHistoricalDataModel> => {
  const { data } = await AxiosInstance.get<LawHistoricalDataModel>(
    getLawHistoricalDataURL(params),
  );

  return data;
};

export interface FetchSrriPageParams {
  fundName: string;
  gmidShareclass?: string;
}

export const getSrriPageURL = ({
  fundName,
  gmidShareclass,
}: FetchSrriPageParams): string =>
  gmidShareclass
    ? `srri-page/${fundName}/${gmidShareclass}`
    : `srri-page/${fundName}`;

export const fetchSrriPage = async (
  params: FetchSrriPageParams,
): Promise<SrriPageModel> => {
  const { data } = await AxiosInstance.get<SrriPageModel>(
    getSrriPageURL(params),
  );

  return data;
};

export const getMrmPageURL = (fundName: string): string =>
  `mrm-page/${fundName}`;

export const fetchMrmPage = async (fundName: string): Promise<MrmPageModel> => {
  const { data } = await AxiosInstance.get<MrmPageModel>(
    getMrmPageURL(fundName),
  );

  return data;
};

export interface FetchShareClassHedgeSummaryPageParams {
  positionDate: string;
  fundTypes?: string;
}

export const getShareClassHedgeSummaryPageURL = ({
  positionDate,
  fundTypes,
}: FetchShareClassHedgeSummaryPageParams): string => {
  const queryParams: string[] = [];

  if (fundTypes?.length) {
    queryParams.push(`schemas=${fundTypes}`);
  }

  const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
  return `get_hedge_ratios/${positionDate}${queryString}`;
};

export const fetchShareClassHedgeSummaryPage = async (
  params: FetchShareClassHedgeSummaryPageParams,
): Promise<ShareClassHedgeSummaryPageModel> => {
  const { data } = await AxiosInstance.get<ShareClassHedgeSummaryPageModel>(
    getShareClassHedgeSummaryPageURL(params),
  );

  return data;
};

export const getCollateralStressTestPageURL = (fundName: string): string =>
  `collateral-stress-test-page/${fundName}`;

export const fetchCollateralStressTestPage = async (
  fundName: string,
): Promise<CollateralStressTestPageModel> => {
  const { data } = await AxiosInstance.get<CollateralStressTestPageModel>(
    getCollateralStressTestPageURL(fundName),
  );

  return data;
};

export interface FetchFundPerformanceAnalysisParams {
  date: string;
  aperiod: string;
  fundName: string;
}

export const getFundPerformanceAnalysisPageURL = ({
  date,
  aperiod,
  fundName,
}: FetchFundPerformanceAnalysisParams): string =>
  `fund-performance-analysis/${date}/${aperiod}/${fundName}`;

export const fetchFundPerformanceAnalysisPage = async (
  params: FetchFundPerformanceAnalysisParams,
): Promise<FundPerformanceAnalysisPageModel> => {
  const { data } = await AxiosInstance.get<FundPerformanceAnalysisPageModel>(
    getFundPerformanceAnalysisPageURL(params),
  );

  return data;
};

export const getRiskComparisonPageURL = (
  fundName: string,
  fromDate: string,
  toDate: string,
): string => `risk_comparison_page/${fundName}/${fromDate}/${toDate}`;

export const fetchRiskComparisonPage = async (
  fundName: string,
  fromDate: string,
  toDate: string,
): Promise<RiskComparisonPageModel> => {
  const { data } = await AxiosInstance.get<RiskComparisonPageModel>(
    getRiskComparisonPageURL(fundName, fromDate, toDate),
  );

  return data;
};

export const getRelativeRiskPageURL = (
  fundName: string,
  positionDate: string,
): string => `relative_risk_page/${fundName}/${positionDate}`;

export const fetchRelativeRiskPage = async (
  fundName: string,
  positionDate: string,
): Promise<RelativeRiskPageModel> => {
  const { data } = await AxiosInstance.get<RelativeRiskPageModel>(
    getRelativeRiskPageURL(fundName, positionDate),
  );

  return data;
};

export const fetchSrriShareClassPage = async (
  fundName: string,
): Promise<SrriShareClassPageModel> => {
  const { data } = await AxiosInstance.get<SrriShareClassPageModel>(
    `get-srri-share-class-page/${fundName}`,
  );

  return data;
};

/** GET /srri-overview-page/:positionDate — SRRI share-class overview (`OverviewPage`). */
export const getSrriOverviewPageURL = (positionDate: string): string =>
  `srri-overview-page/${positionDate}`;

export const fetchSrriOverviewPage = async (
  positionDate: string,
): Promise<SrriOverviewPageModel> => {
  const { data } = await AxiosInstance.get<SrriOverviewPageModel>(
    getSrriOverviewPageURL(positionDate),
  );

  return data;
};

export const getMarketLookbackPageURL = (
  selectedDate: string,
  lookback: string,
): string => `market-lookback/${selectedDate}/${lookback}`;

export const fetchMarketLookbackPage = async (
  selectedDate: string,
  lookback: string,
): Promise<MarketLookbackPageModel> => {
  const { data } = await AxiosInstance.get<MarketLookbackPageModel>(
    getMarketLookbackPageURL(selectedDate, lookback),
  );

  return data;
};

/** GET /article-48-coverage-rules — Article 48 coverage rules (`OverviewPage`). */
export const getArticle48CoverageRulesPageURL = (): string =>
  'article-48-coverage-rules';

export const fetchArticle48CoverageRulesPage =
  async (): Promise<Article48CoverageRulesPageModel> => {
    const { data } = await AxiosInstance.get<Article48CoverageRulesPageModel>(
      getArticle48CoverageRulesPageURL(),
    );

    return data;
  };

/** GET /global-exposure[/:positionDate] — global exposure (`OverviewPage`). */
export const getGlobalExposurePageURL = (positionDate?: string): string =>
  positionDate ? `global-exposure/${positionDate}` : 'global-exposure';

export const fetchGlobalExposurePage = async (
  positionDate?: string,
): Promise<GlobalExposurePageModel> => {
  const { data } = await AxiosInstance.get<GlobalExposurePageModel>(
    getGlobalExposurePageURL(positionDate),
  );

  return data;
};

/**
 * Fetch CSSF risk spreading fund page.
 * GET /stored-cssf-risk-spreading-monitor/:fundName/:positionDate
 */
export const getCssfRiskSpreadingURL = (
  fundName: string,
  positionDate: string,
) => `stored-cssf-risk-spreading-monitor/${fundName}/${positionDate}`;

export const fetchCssfRiskSpreadingPage = async (
  fundName: string,
  positionDate: string,
): Promise<CssfRiskSpreadingPageModel> => {
  const { data } = await AxiosInstance.get<CssfRiskSpreadingPageModel>(
    getCssfRiskSpreadingURL(fundName, positionDate),
  );
  return data;
};

/**
 * Fetch CSSF in-scope positions for a rule (detail panel).
 * GET /cssf_risk_spreading_inscope_positions/:fundName/:ruleNumber/:positionDate?
 */
export const getCssfInscopePositionsURL = (
  fundName: string,
  ruleNumber: number,
  positionDate?: string,
) =>
  `cssf_risk_spreading_inscope_positions/${fundName}/${ruleNumber}${
    positionDate ? `/${positionDate}` : '/'
  }`;

export const fetchCssfInscopePositions = async (
  fundName: string,
  ruleNumber: number,
  positionDate?: string,
): Promise<CssfInscopePositionsResponse> => {
  const { data } = await AxiosInstance.get<CssfInscopePositionsResponse>(
    getCssfInscopePositionsURL(fundName, ruleNumber, positionDate),
  );
  return data;
};

/**
 * VaR vs internal limit for every authorised VaR fund.
 * GET /var-limit-overview/:selectedPositionDate
 */
export const getVarLimitOverviewURL = (
  selectedPositionDate: string,
  raptorPlatformId?: string,
): string => {
  const params = new URLSearchParams();
  if (raptorPlatformId) {
    params.set('raptor_platform_id', raptorPlatformId);
  }
  const query = params.toString();
  return `var-limit-overview/${selectedPositionDate}${query ? `?${query}` : ''}`;
};

export const fetchVarLimitOverview = async (
  selectedPositionDate: string,
  raptorPlatformId?: string,
): Promise<VarLimitOverviewPageModel> => {
  const { data } = await AxiosInstance.get<VarLimitOverviewPageModel>(
    getVarLimitOverviewURL(selectedPositionDate, raptorPlatformId),
  );
  return data;
};

/** GET /upplementary-rules/<fund_name>/<position_date> */
export const getSupplementaryRulesPageURL = (
  fundName: string,
  positionDate: string,
): string => `supplementary-rules/${fundName}/${positionDate}`;

export const fetchSupplementaryRulesPage = async (
  fundName: string,
  positionDate: string,
): Promise<SupplementaryRulesPageModel> => {
  const { data } = await AxiosInstance.get<SupplementaryRulesPageModel>(
    getSupplementaryRulesPageURL(fundName, positionDate),
  );
  return data;
};
